import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"

export default class extends Controller {
  static values = { positions: Array, laps: Array }

  _cssVar(name) {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim()
  }

  connect() {
    this._render()
    this._onAppearanceChange = () => {
      this.element.innerHTML = ""
      if (this._tooltip) {
        this._tooltip.remove()
        this._tooltip = null
      }
      this._render()
    }
    window.addEventListener("appearance:change", this._onAppearanceChange)
  }

  disconnect() {
    if (this._tooltip) {
      this._tooltip.remove()
      this._tooltip = null
    }
    window.removeEventListener("appearance:change", this._onAppearanceChange)
  }

  _render() {
    const positions = this.positionsValue
    const laps = this.lapsValue
    if (positions.length < 2) return

    const width = 80
    const height = 24
    const pad = 2

    this.element.style.position = "relative"

    const x = d3
      .scaleLinear()
      .domain([0, positions.length - 1])
      .range([pad, width - pad])

    // Inverted Y: P1 at top, worst position at bottom
    const [minPos, maxPos] = d3.extent(positions)
    const y = d3
      .scaleLinear()
      .domain([maxPos, minPos])
      .range([height - pad, pad])

    const line = d3
      .line()
      .x((_, i) => x(i))
      .y((d) => y(d))
      .curve(d3.curveMonotoneX)

    const midY = (pad + height - pad) / 2

    const svg = d3
      .select(this.element)
      .append("svg")
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("class", "w-20 h-6")
      .attr("preserveAspectRatio", "none")

    svg
      .append("line")
      .attr("x1", pad)
      .attr("x2", width - pad)
      .attr("y1", midY)
      .attr("y2", midY)
      .attr("stroke", this._cssVar("--app-border"))
      .attr("stroke-width", 0.5)

    svg
      .append("path")
      .datum(positions)
      .attr("d", line)
      .attr("fill", "none")
      .attr("stroke", this._cssVar("--color-electric-blue"))
      .attr("stroke-width", 1.5)

    // Hover elements
    const indicator = svg
      .append("line")
      .attr("y1", 0)
      .attr("y2", height)
      .attr("stroke", this._cssVar("--app-text-faint"))
      .attr("stroke-width", 0.5)
      .attr("stroke-dasharray", "2,1")
      .attr("display", "none")
      .style("transition", "x1 80ms ease-out, x2 80ms ease-out")

    const dot = svg
      .append("circle")
      .attr("r", 2)
      .attr("fill", this._cssVar("--color-electric-blue"))
      .attr("display", "none")
      .style("transition", "cx 80ms ease-out, cy 80ms ease-out")

    // DOM tooltip (fixed position so it escapes overflow containers)
    const tooltip = document.createElement("div")
    Object.assign(tooltip.style, {
      position: "fixed",
      padding: "2px 5px",
      background: this._cssVar("--app-text"),
      color: this._cssVar("--app-surface"),
      fontSize: "10px",
      fontFamily: "ui-monospace, monospace",
      borderRadius: "3px",
      whiteSpace: "nowrap",
      pointerEvents: "none",
      display: "none",
      zIndex: "50",
      transition: "left 80ms ease-out, top 80ms ease-out",
    })
    document.body.appendChild(tooltip)
    this._tooltip = tooltip

    // Invisible overlay for mouse events
    svg
      .append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "transparent")
      .on("mousemove", (event) => {
        const [mx] = d3.pointer(event)
        const idx = Math.round(x.invert(mx))
        const clamped = Math.max(0, Math.min(positions.length - 1, idx))

        const cx = x(clamped)
        const cy = y(positions[clamped])

        indicator.attr("x1", cx).attr("x2", cx).attr("display", null)
        dot.attr("cx", cx).attr("cy", cy).attr("display", null)

        const lapNum = laps[clamped] || clamped + 1
        tooltip.textContent = `Lap ${lapNum}: P${positions[clamped]}`
        tooltip.style.display = "block"

        // Position tooltip snapped to the data point's x position, clamped to viewport
        const svgEl = svg.node()
        const svgRect = svgEl.getBoundingClientRect()
        const scaleX = svgRect.width / width
        const snapScreenX = svgRect.left + cx * scaleX
        const elRect = this.element.getBoundingClientRect()
        const tipW = tooltip.offsetWidth
        const tipH = tooltip.offsetHeight
        const gap = 4

        let left = snapScreenX - tipW / 2
        left = Math.max(gap, Math.min(left, window.innerWidth - tipW - gap))

        let top = elRect.top - tipH - gap
        if (top < gap) {
          top = elRect.bottom + gap
        }

        tooltip.style.left = `${left}px`
        tooltip.style.top = `${top}px`
      })
      .on("mouseleave", () => {
        indicator.attr("display", "none")
        dot.attr("display", "none")
        tooltip.style.display = "none"
      })
  }
}
